import {
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle2,
  ExternalLink,
  Gamepad2,
  Globe2,
  Search,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { loadHotGuidesStatus, trackSiteEvent } from "./api";
import type { HotGuideStatusPage } from "./types";

type Guide = {
  category: "AI tools" | "Games";
  deck: string;
  image: string;
  marketSignal: string;
  path: string;
  readingTime: string;
  title: string;
  updated: string;
};

type Article = Guide & {
  checklist: string[];
  intro: string;
  sources: Array<{ label: string; url: string }>;
  steps: Array<{ body: string; label: string; title: string }>;
  takeaways: string[];
};

const guides: Guide[] = [
  {
    category: "Games",
    deck:
      "A safe workflow for checking Grow a Garden code pages, redeeming rewards and avoiding stale Roblox lists.",
    image: "/assets/hot-guides/game-guide-visual.png",
    marketSignal: "Roblox guide pages are being updated daily by major gaming sites.",
    path: "/hot-guides/roblox-grow-a-garden-codes-beginner-guide/",
    readingTime: "6 min",
    title: "Roblox Grow a Garden Codes and Beginner Guide",
    updated: "Last checked June 12, 2026"
  },
  {
    category: "AI tools",
    deck:
      "A practical way to choose AI tools by traffic, use case and workflow value instead of chasing every launch.",
    image: "/assets/hot-guides/ai-tools-guide-visual.png",
    marketSignal: "ChatGPT, Gemini and other AI tools remain high-traffic tutorial targets.",
    path: "/hot-guides/best-ai-tools-2026/",
    readingTime: "7 min",
    title: "Best AI Tools to Try in 2026",
    updated: "Last checked June 12, 2026"
  },
  {
    category: "Games",
    deck:
      "Use Steam charts as a signal, not a command. Learn how to separate real player momentum from launch spikes.",
    image: "/assets/hot-guides/game-guide-visual.png",
    marketSignal: "Steam publishes official current and peak player charts.",
    path: "/hot-guides/steam-most-played-games-guide/",
    readingTime: "5 min",
    title: "Steam Most Played Games Guide",
    updated: "Last checked June 12, 2026"
  },
  {
    category: "Games",
    deck:
      "A safer source-checking workflow for Roblox code guides, especially when a game is trending fast.",
    image: "/assets/hot-guides/game-guide-visual.png",
    marketSignal: "Roblox code pages attract search demand but expire quickly.",
    path: "/hot-guides/roblox-game-codes-safe-sources/",
    readingTime: "5 min",
    title: "How to Find Safe Roblox Game Codes",
    updated: "Last checked June 12, 2026"
  }
];

const articles: Record<string, Article> = {
  "/hot-guides/roblox-grow-a-garden-codes-beginner-guide/": {
    ...guides[0],
    checklist: [
      "Check at least two current gaming sources before trusting a code list.",
      "Never install a browser extension, file or generator to redeem a Roblox code.",
      "Prefer official game pages, developer socials and major editorial sites.",
      "Treat every code as time-sensitive and expect expiry without warning."
    ],
    intro:
      "Grow a Garden is a good first target for a guide site because it combines a popular Roblox game loop with recurring code-search intent. The business angle is simple: code guides are easy to understand, image-heavy and update-driven, but they need strict freshness discipline.",
    sources: [
      { label: "Roblox Charts", url: "https://www.roblox.com/charts" },
      { label: "PC Gamer Grow a Garden codes", url: "https://www.pcgamer.com/games/roblox/grow-a-garden-codes/" },
      { label: "PCGamesN Grow a Garden codes", url: "https://www.pcgamesn.com/roblox/grow-a-garden-codes" }
    ],
    steps: [
      {
        body:
          "Start with a current editorial source, then compare it with the game page or developer channels. If the page was not updated recently, do not trust the list.",
        label: "Step 1",
        title: "Check freshness before copying codes"
      },
      {
        body:
          "Open the Roblox experience from the official Roblox page. Never click a third-party generator or a download link just to claim rewards.",
        label: "Step 2",
        title: "Redeem inside the game only"
      },
      {
        body:
          "Write down whether a code worked, failed or was already redeemed. That note becomes the next update for the guide.",
        label: "Step 3",
        title: "Track the result"
      }
    ],
    takeaways: [
      "This is a high-update guide format, not evergreen content.",
      "The safest monetization is display ads and related guide navigation, not fake code generators.",
      "A daily freshness label matters more than long copy."
    ]
  },
  "/hot-guides/best-ai-tools-2026/": {
    ...guides[1],
    checklist: [
      "Choose AI tools by workflow: research, writing, coding, image, video or presentation.",
      "Use traffic rankings as a discovery signal, not proof that a tool is best.",
      "Check pricing, privacy and export options before moving real work into any tool.",
      "Capture screenshots only for tutorial context and keep them current."
    ],
    intro:
      "AI tool tutorials are more monetizable than most game guides because readers often have software budgets. The tradeoff is competition and fast-changing UI. The best content angle is not a generic list; it is a workflow comparison that helps readers choose the right tool for a specific job.",
    sources: [
      { label: "SimilarWeb AI Chatbots and Tools ranking", url: "https://www.similarweb.com/top-websites/ai-chatbots-and-tools/" },
      { label: "a16z Top 100 Gen AI Consumer Apps", url: "https://a16z.com/100-gen-ai-apps-6/" },
      { label: "Google Trends", url: "https://trends.google.com/trends/" }
    ],
    steps: [
      {
        body:
          "Group tools by job before naming winners: chatbot, research assistant, creator tool, video tool, design tool and coding tool.",
        label: "Step 1",
        title: "Start with a use case"
      },
      {
        body:
          "Check traffic rankings and trend tools to decide what readers are already searching for. High traffic means tutorial demand, not automatic quality.",
        label: "Step 2",
        title: "Validate demand"
      },
      {
        body:
          "Write one visual workflow per tool: input, action, output, limitations and cost. Screenshots should show steps, not marketing slogans.",
        label: "Step 3",
        title: "Turn comparison into tutorial"
      }
    ],
    takeaways: [
      "Start with high-traffic tools, but win with workflow depth.",
      "Best first articles: AI chatbot comparison, AI research workflow, AI video workflow.",
      "Affiliate links are possible later, but trust comes from honest limitations."
    ]
  },
  "/hot-guides/steam-most-played-games-guide/": {
    ...guides[2],
    checklist: [
      "Use official Steam charts before writing a most-played guide.",
      "Separate current players from 24-hour peak and all-time peak.",
      "Watch whether a game is free-to-play, newly launched or event-driven.",
      "Avoid promising that a trending game is worth buying for every player."
    ],
    intro:
      "Steam chart guides are a good tutorial format because readers want a simple answer: what is popular, why is it popular, and whether it is worth trying. The defensible angle is teaching readers how to interpret the chart rather than blindly repeating a top ten list.",
    sources: [
      { label: "Official Steam Charts", url: "https://store.steampowered.com/charts/mostplayed" },
      { label: "Steam Top Sellers", url: "https://store.steampowered.com/charts/topselling/global" },
      { label: "Steam News", url: "https://store.steampowered.com/news/" }
    ],
    steps: [
      {
        body:
          "Open the official most-played chart and look at current players first. This shows what is active now, not what had a historic launch spike.",
        label: "Step 1",
        title: "Read current players"
      },
      {
        body:
          "Compare current players with 24-hour peak. If the gap is extreme, timezone or event timing may be shaping the chart.",
        label: "Step 2",
        title: "Check the peak gap"
      },
      {
        body:
          "Before recommending a paid game, check recent reviews, genre fit and whether the current spike came from a sale or update.",
        label: "Step 3",
        title: "Add buying context"
      }
    ],
    takeaways: [
      "Official charts are better than scraped chart screenshots.",
      "This content can update weekly without heavy production cost.",
      "The strongest monetization is ads, affiliate game deals and related guide clusters."
    ]
  },
  "/hot-guides/roblox-game-codes-safe-sources/": {
    ...guides[3],
    checklist: [
      "Use official Roblox pages and developer channels as the safest starting point.",
      "Avoid code generators, Robux promises, downloads and login prompts.",
      "Prefer guides that show a last checked date.",
      "For younger players, keep privacy and account safety advice visible."
    ],
    intro:
      "Roblox code searches can produce fast traffic, but the space is noisy. A useful guide site should help readers check sources safely rather than pushing fake code tools. This is especially important because many Roblox players are young.",
    sources: [
      { label: "Roblox Charts", url: "https://www.roblox.com/charts" },
      { label: "Roblox Community Standards", url: "https://en.help.roblox.com/hc/en-us/articles/203313410-Roblox-Community-Standards" },
      { label: "PC Gamer Roblox code guides", url: "https://www.pcgamer.com/games/roblox/" }
    ],
    steps: [
      {
        body:
          "Find the official game page first. Use it to confirm the game name, developer and whether the experience is still active.",
        label: "Step 1",
        title: "Confirm the game"
      },
      {
        body:
          "Check a recent editorial guide and developer channels. A code page without a date should be treated as stale.",
        label: "Step 2",
        title: "Verify the code source"
      },
      {
        body:
          "Close pages that ask for passwords, Robux generators, downloads or browser extensions. Real codes do not require any of those.",
        label: "Step 3",
        title: "Reject risky pages"
      }
    ],
    takeaways: [
      "Safety guidance is part of the content value.",
      "Code pages need an update calendar, not a one-time publish.",
      "Do not collect emails or personal data from Roblox guide readers."
    ]
  }
};

const marketPicks = [
  {
    category: "First cluster",
    decision: "Roblox code and beginner guides",
    reason:
      "Fast search intent, simple tutorial format, frequent updates and strong internal-link potential.",
    score: "31/35"
  },
  {
    category: "Second cluster",
    decision: "AI tool workflow guides",
    reason:
      "Higher affiliate and B2B value, but more competition and stronger screenshot freshness requirements.",
    score: "29/35"
  },
  {
    category: "Support cluster",
    decision: "Steam chart explainers",
    reason:
      "Reliable official source, weekly update cadence and evergreen tutorial angle.",
    score: "25/35"
  }
];

function articleFromPath(pathname: string) {
  return articles[pathname.endsWith("/") ? pathname : `${pathname}/`];
}

function attribution() {
  const params = new URLSearchParams(window.location.search);

  return {
    source_path: `${window.location.pathname}${window.location.search}`,
    utm_campaign: params.get("utm_campaign"),
    utm_medium: params.get("utm_medium"),
    utm_source: params.get("utm_source")
  };
}

function trackHotGuideEvent(eventName: string, target?: string, metadata: Record<string, unknown> = {}) {
  void trackSiteEvent({
    event_name: eventName,
    metadata,
    referrer: document.referrer,
    site_id: "site_hot_guides",
    target,
    ...attribution()
  }).catch(() => undefined);
}

function formatCheckedAt(value?: string | null) {
  if (!value) return "Not checked by automation yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `Automation checked ${parsed.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric"
  })}`;
}

function statusFor(path: string, statuses: HotGuideStatusPage[]) {
  const normalized = path.endsWith("/") ? path : `${path}/`;
  return statuses.find((item) => item.path === normalized);
}

function guideTitle(guide: Guide, statuses: HotGuideStatusPage[]) {
  return statusFor(guide.path, statuses)?.title_override ?? guide.title;
}

function HotGuidesNav() {
  return (
    <nav className="hot-guides-nav">
      <a className="hot-guides-brand" href="/hot-guides/">
        <span>HG</span>
        <strong>Hot Guides</strong>
      </a>
      <div>
        <a href="/hot-guides/#market">Market Radar</a>
        <a href="/hot-guides/#guides">Guides</a>
        <a href="/hot-guides/best-ai-tools-2026/">AI Tools</a>
        <a href="/hot-guides/roblox-grow-a-garden-codes-beginner-guide/">Games</a>
      </div>
    </nav>
  );
}

function SourceLink({ label, pageTitle, url }: { label: string; pageTitle: string; url: string }) {
  return (
    <a
      href={url}
      onClick={() => trackHotGuideEvent("hot_guide_source_click", url, { label, pageTitle })}
      rel="noreferrer"
      target="_blank"
    >
      {label}
      <ExternalLink size={14} />
    </a>
  );
}

function ScreenshotPanel({ title, rows }: { title: string; rows: string[] }) {
  return (
    <div className="tutorial-screenshot" aria-label={title}>
      <div className="screenshot-bar">
        <span />
        <span />
        <span />
      </div>
      <strong>{title}</strong>
      <div className="screenshot-rows">
        {rows.map((row, index) => (
          <div key={row}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <p>{row}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function HotGuidesArticle({ article }: { article: Article }) {
  const [statuses, setStatuses] = useState<HotGuideStatusPage[]>([]);
  const didTrack = useRef(false);
  const didTrackScroll = useRef(false);
  const status = statusFor(window.location.pathname, statuses);
  const displayTitle = status?.title_override ?? article.title;
  const displayIntro = status?.hero_intro_override ?? article.intro;

  useEffect(() => {
    document.title = `${displayTitle} | Hot Guides`;
  }, [displayTitle]);

  useEffect(() => {
    void loadHotGuidesStatus()
      .then((next) => setStatuses(next.pages))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (didTrack.current) return;
    didTrack.current = true;
    trackHotGuideEvent("hot_guide_page_view", article.path, {
      articleTitle: article.title,
      category: article.category
    });

    function onScroll() {
      if (didTrackScroll.current) return;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) return;
      const progress = window.scrollY / maxScroll;
      if (progress >= 0.6) {
        didTrackScroll.current = true;
        trackHotGuideEvent("hot_guide_scroll_60", article.path, { articleTitle: article.title });
        window.removeEventListener("scroll", onScroll);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [article.category, article.path, article.title]);

  return (
    <main className="hot-guides-page">
      <HotGuidesNav />
      <article className="hot-article">
        <header className="hot-article-hero">
          <div>
            <p className="hot-eyebrow">{article.category} tutorial</p>
            <h1>{displayTitle}</h1>
            <p>{displayIntro}</p>
            <div className="hot-meta">
              <span>{formatCheckedAt(status?.last_checked_at) || article.updated}</span>
              <span>{status?.last_source_summary ?? "Source checks pending"}</span>
              <span>{status?.last_decision?.replaceAll("_", " ") ?? "collecting data"}</span>
              {status?.title_override && <span>Auto title variant active</span>}
              <span>{article.readingTime} read</span>
              <span>Independent guide, not affiliated with mentioned brands.</span>
            </div>
          </div>
          <img alt="" src={article.image} />
        </header>

        <section className="hot-section hot-two-column">
          <div>
            <h2>Market signal</h2>
            <p>{article.marketSignal}</p>
            <div className="source-list hot-source-list">
              {article.sources.map((source) => (
                <SourceLink key={source.url} pageTitle={displayTitle} {...source} />
              ))}
            </div>
          </div>
          <ScreenshotPanel
            title="Tutorial snapshot"
            rows={article.steps.map((step) => step.title)}
          />
        </section>

        <section className="hot-section">
          <h2>Step-by-step workflow</h2>
          <div className="hot-step-grid">
            {article.steps.map((step) => (
              <article key={step.title}>
                <span>{step.label}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="hot-section hot-two-column">
          <div>
            <h2>Safety and quality checklist</h2>
            <ul className="hot-check-list">
              {article.checklist.map((item) => (
                <li key={item}>
                  <CheckCircle2 size={18} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2>Operator takeaways</h2>
            <ul className="hot-check-list">
              {article.takeaways.map((item) => (
                <li key={item}>
                  <ShieldCheck size={18} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="hot-section hot-cta-band">
          <h2>Build this into a content cluster</h2>
          <p>
            Publish one guide, record search clicks and update freshness daily for code pages or weekly
            for chart pages. If the article gets impressions but weak clicks, rewrite the title before
            adding more pages.
          </p>
          <a href="/hot-guides/">Back to market radar</a>
        </section>
      </article>
    </main>
  );
}

export function HotGuidesPage() {
  const article = articleFromPath(window.location.pathname);
  const [statuses, setStatuses] = useState<HotGuideStatusPage[]>([]);
  const didTrack = useRef(false);

  const groupedGuides = useMemo(
    () => ({
      ai: guides.filter((guide) => guide.category === "AI tools"),
      games: guides.filter((guide) => guide.category === "Games")
    }),
    []
  );

  useEffect(() => {
    if (!article) {
      document.title = "Hot Tool and Game Guides | Market Signal Tutorials";
    }
  }, [article]);

  useEffect(() => {
    if (article) return;
    void loadHotGuidesStatus()
      .then((next) => setStatuses(next.pages))
      .catch(() => undefined);
  }, [article]);

  useEffect(() => {
    if (article || didTrack.current) return;
    didTrack.current = true;
    trackHotGuideEvent("hot_guide_page_view", "/hot-guides/", { articleTitle: "Hot Guides hub" });
  }, [article]);

  if (article) return <HotGuidesArticle article={article} />;

  return (
    <main className="hot-guides-page">
      <HotGuidesNav />

      <section className="hot-hero">
        <img alt="" src="/assets/hot-guides/hot-guides-hero.png" />
        <div className="hot-hero-copy">
          <p className="hot-eyebrow">Market-first tutorials</p>
          <h1>Guides for the tools and games people are already searching for.</h1>
          <p>
            This hub tracks high-demand AI tools, Roblox games and Steam chart signals, then turns
            them into visual tutorials with source links, update discipline and safety notes.
          </p>
          <div className="hot-hero-actions">
            <a href="#guides">
              View guides
              <ArrowRight size={18} />
            </a>
            <a href="#market">See market logic</a>
          </div>
        </div>
      </section>

      <section className="hot-section hot-signal-strip" id="market">
        <article>
          <Search size={20} />
          <strong>SEO intent</strong>
          <p>Codes, rankings, comparisons and beginner guides create repeatable long-tail pages.</p>
        </article>
        <article>
          <BarChart3 size={20} />
          <strong>Update cadence</strong>
          <p>Game code pages need daily checks; AI tool and Steam chart guides can update weekly.</p>
        </article>
        <article>
          <ShieldCheck size={20} />
          <strong>Risk control</strong>
          <p>No fake generators, no downloads, no account collection and no unaffiliated endorsement claims.</p>
        </article>
      </section>

      <section className="hot-section">
        <div className="hot-section-heading">
          <div>
            <p className="hot-eyebrow">Decision radar</p>
            <h2>What to build first</h2>
          </div>
          <span>Scored for traffic potential, effort, freshness and monetization.</span>
        </div>
        <div className="hot-market-grid">
          {marketPicks.map((pick) => (
            <article key={pick.decision}>
              <span>{pick.category}</span>
              <h3>{pick.decision}</h3>
              <p>{pick.reason}</p>
              <strong>{pick.score}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="hot-section" id="guides">
        <div className="hot-section-heading">
          <div>
            <p className="hot-eyebrow">Published MVP pages</p>
            <h2>Visual tutorial queue</h2>
          </div>
          <span>External-market content, not our own product documentation.</span>
        </div>
        <div className="hot-guide-grid">
          {guides.map((guide) => (
            <article key={guide.path}>
              <img alt="" src={guide.image} />
              <div>
                <span>{guide.category}</span>
                <h3>{guideTitle(guide, statuses)}</h3>
                <p>{guide.deck}</p>
                <small>{guide.marketSignal}</small>
                <a
                  href={guide.path}
                  onClick={() =>
                    trackHotGuideEvent("hot_guide_internal_click", guide.path, {
                      from: "guide_grid",
                      title: guideTitle(guide, statuses)
                    })
                  }
                >
                  Open guide
                  <ArrowRight size={16} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="hot-section hot-two-column">
        <div>
          <p className="hot-eyebrow">Tool track</p>
          <h2>
            <Bot size={22} />
            AI tool tutorials
          </h2>
          <div className="hot-mini-list">
            {groupedGuides.ai.map((guide) => (
              <a
                href={guide.path}
                key={guide.path}
                onClick={() =>
                  trackHotGuideEvent("hot_guide_internal_click", guide.path, {
                    from: "ai_track",
                    title: guideTitle(guide, statuses)
                  })
                }
              >
                {guideTitle(guide, statuses)}
                <Sparkles size={16} />
              </a>
            ))}
          </div>
        </div>
        <div>
          <p className="hot-eyebrow">Game track</p>
          <h2>
            <Gamepad2 size={22} />
            Game guide tutorials
          </h2>
          <div className="hot-mini-list">
            {groupedGuides.games.map((guide) => (
              <a
                href={guide.path}
                key={guide.path}
                onClick={() =>
                  trackHotGuideEvent("hot_guide_internal_click", guide.path, {
                    from: "game_track",
                    title: guideTitle(guide, statuses)
                  })
                }
              >
                {guideTitle(guide, statuses)}
                <Globe2 size={16} />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="hot-section">
        <div className="hot-section-heading">
          <div>
            <p className="hot-eyebrow">Automation status</p>
            <h2>Daily freshness and stop-line checks</h2>
          </div>
          <span>Runs daily through Cloudflare Workers Cron. Search clicks are referrer-based until GSC is connected.</span>
        </div>
        <div className="hot-market-grid">
          {statuses.slice(0, 3).map((item) => (
            <article key={item.id}>
              <span>{item.topic_type.replaceAll("_", " ")}</span>
              <h3>{item.title_override ?? item.title}</h3>
              <p>{item.last_source_summary ?? "Source checks pending."}</p>
              <strong>{item.last_decision.replaceAll("_", " ")}</strong>
            </article>
          ))}
          {statuses.length === 0 && (
            <article>
              <span>pending</span>
              <h3>Automation has not reported yet</h3>
              <p>The scheduled worker will publish the first freshness status after its first run.</p>
              <strong>collecting</strong>
            </article>
          )}
        </div>
      </section>
    </main>
  );
}
